import { useState } from "react";
import { Link } from "wouter";
import { ChevronRight, Save, Trash2, ArrowLeft } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SearchCombobox } from "@/components/ui/search-combobox";
import { searchModels } from "@/lib/snipeit/lookups";
import { useLocationDetail } from "@/common/api/use-location-detail";
import { useDeployableLabel } from "@/common/api/use-deployable-label";
import { useBookinHardware } from "@/common/api/use-bookin-hardware";
import type { SnipeItModel } from "@/lib/snipeit/types";
import { toast } from "sonner";
import { useLocation } from "wouter";

interface BookInItem {
  id: string; // unique temp id
  serial: string;
  assetTag: string;
}

export function BookinDeviceView() {
  const { location: savedLocation } = useLocationDetail();
  const { data: deployableLabel, isLoading: isLabelLoading } = useDeployableLabel();
  const bookinMutation = useBookinHardware();
  const [, navigate] = useLocation();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedModel, setSelectedModel] = useState<SnipeItModel | null>(null);
  const [modelInput, setModelInput] = useState("");

  const [items, setItems] = useState<BookInItem[]>([]);
  const [currentSerial, setCurrentSerial] = useState("");
  const [currentAssetTag, setCurrentAssetTag] = useState("");

  // Step 2 Logic
  const handleAddNext = () => {
    if (!currentSerial || !currentAssetTag) return false;

    // Check for duplicates
    const duplicateSerial = items.some((item) => item.serial === currentSerial);
    const duplicateAssetTag = items.some((item) => item.assetTag === currentAssetTag);

    if (duplicateSerial && duplicateAssetTag) {
      toast.error(
        `Achtung: Die Seriennummer "${currentSerial}" und der Asset Tag "${currentAssetTag}" wurden bereits gescannt!`,
      );
      return false;
    } else if (duplicateSerial) {
      toast.error(`Achtung: Die Seriennummer "${currentSerial}" wurde bereits gescannt!`);
      return false;
    } else if (duplicateAssetTag) {
      toast.error(`Achtung: Der Asset Tag "${currentAssetTag}" wurde bereits gescannt!`);
      return false;
    }

    setItems((prev) => [...prev, { id: String(Date.now()), serial: currentSerial, assetTag: currentAssetTag }]);
    setCurrentSerial("");

    const numericTag = parseInt(currentAssetTag, 10);
    if (!isNaN(numericTag)) {
      setCurrentAssetTag((numericTag + 1).toString().padStart(currentAssetTag.length, "0"));
    } else {
      setCurrentAssetTag("");
    }

    return true;
  };

  const handleDone = () => {
    if (currentSerial && currentAssetTag) {
      const added = handleAddNext();
      if (!added) return;
    }
    setStep(3);
  };

  // Step 3 Logic
  const handleRemoveItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const submitItems = async () => {
    if (!selectedModel || !savedLocation || !deployableLabel) {
      toast.error(!deployableLabel ? "Es konnte kein Statuslabel für 'Bereit' gefunden werden." : "Fehlende Daten.");
      return;
    }

    bookinMutation.mutate(
      items.map((item) => ({
        model_id: selectedModel.id,
        status_id: deployableLabel.id,
        rtd_location_id: savedLocation.id,
        location_id: savedLocation.id,
        serial: item.serial,
        asset_tag: item.assetTag,
      })),
      {
        onSuccess: (res) => {
          if (res.failures.length === 0) {
            toast.success(`${res.successes.length} Gerät(e) erfolgreich eingebucht.`);
            navigate("/");
          } else {
            toast.error(`Fehler: ${res.failures.length} Gerät(e) konnten nicht eingebucht werden.`);
            if (res.successes.length > 0) {
              toast.success(`${res.successes.length} Gerät(e) erfolgreich eingebucht.`);
            }

            // Remove successfully created items from the list so the user can easily retry the failed ones
            const successfulSerials = new Set(res.successes.map((s) => s.serial));
            const successfulAssetTags = new Set(res.successes.map((s) => s.asset_tag));

            setItems((prev) =>
              prev.filter((i) => !successfulSerials.has(i.serial) && !successfulAssetTags.has(i.assetTag)),
            );
          }
        },
      },
    );
  };

  return (
    <main className="bg-background flex h-dvh items-start justify-center overflow-hidden px-4 pt-6 pb-6">
      <div className="flex h-full w-full max-w-2xl flex-col gap-4">
        {!savedLocation && (
          <div className="border-border bg-card rounded-xl border p-6 text-center shadow-sm">
            <p className="text-muted-foreground text-sm">
              Bitte zuerst einen Standort in den{" "}
              <Link href="/settings" className="text-primary underline">
                Einstellungen
              </Link>{" "}
              festlegen, damit Geräte dort eingebucht werden können.
            </p>
          </div>
        )}

        {savedLocation && (
          <>
            <div className="flex items-center justify-between">
              <Link href="/" className={buttonVariants({ variant: "ghost", size: "sm" })}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Abbrechen
              </Link>
              <h1 className="text-foreground text-lg font-semibold">Einbuchen</h1>
              <div className="text-muted-foreground text-sm font-medium">Schritt {step} von 3</div>
            </div>

            {/* Step 1: Model Selection */}
            {step === 1 && (
              <Card className="border-border shadow-sm">
                <CardHeader>
                  <CardTitle>1. Modell auswählen</CardTitle>
                  <CardDescription>Wähle das Gerätemodell aus, das du einbuchen möchtest.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-muted-foreground text-xs font-medium">Gerätemodell</label>
                    <SearchCombobox
                      placeholder="Modell suchen…"
                      queryKey="models"
                      searchFn={(query) =>
                        searchModels(query).then((results) =>
                          results.map((m) => ({
                            id: m.id,
                            label: m.name,
                            sublabel: m.model_number ?? undefined,
                            data: m,
                          })),
                        )
                      }
                      value={modelInput}
                      onInputChange={(val) => {
                        setModelInput(val);
                        if (selectedModel && val !== selectedModel.name) setSelectedModel(null);
                      }}
                      onSelect={(result) => {
                        const model = result.data as SnipeItModel;
                        setSelectedModel(model);
                        setModelInput(model.name);
                      }}
                    />
                  </div>
                  <div className="flex justify-end pt-4">
                    <Button disabled={!selectedModel} onClick={() => setStep(2)}>
                      Weiter <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Details */}
            {step === 2 && (
              <Card className="border-border shadow-sm">
                <CardHeader>
                  <CardTitle>2. Gerätedaten erfassen</CardTitle>
                  <CardDescription>
                    Gebe Seriennummer und Asset Tag für das nächste {selectedModel?.name} ein.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-muted-foreground text-xs font-medium">Seriennummer</label>
                      <Input
                        placeholder="z.B. C02XYZ123…"
                        value={currentSerial}
                        autoFocus
                        onChange={(e) => setCurrentSerial(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-muted-foreground text-xs font-medium">Asset Tag</label>
                      <Input
                        placeholder="z.B. 012345…"
                        value={currentAssetTag}
                        onChange={(e) => setCurrentAssetTag(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddNext();
                        }}
                      />
                    </div>
                  </div>

                  <div className="bg-accent/50 mt-4 rounded-lg p-3 text-center text-sm font-medium">
                    Zuvor erfasst: <span className="text-primary">{items.length} Geräte</span>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={() => setStep(1)}>
                      Zurück
                    </Button>
                    <div className="flex gap-2">
                      <Button variant="secondary" disabled={!currentSerial || !currentAssetTag} onClick={handleAddNext}>
                        Nächstes Gerät
                      </Button>
                      <Button onClick={handleDone}>
                        Abschließen <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Summarize */}
            {step === 3 && (
              <Card className="border-border flex max-h-full flex-col shadow-sm">
                <CardHeader>
                  <CardTitle>3. Zusammenfassung</CardTitle>
                  <CardDescription>
                    Diese {items.length} {selectedModel?.name}(s) werden am Standort &bdquo;{savedLocation?.name}&ldquo;
                    eingebucht.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 space-y-2 overflow-y-auto">
                  {items.length === 0 ? (
                    <div className="text-muted-foreground py-8 text-center">Keine Geräte erfasst.</div>
                  ) : (
                    items.map((item) => (
                      <div key={item.id} className="bg-accent/50 flex items-center justify-between rounded-md p-3">
                        <div className="text-sm">
                          <div className="font-medium">{item.serial}</div>
                          <div className="text-muted-foreground text-xs">{item.assetTag}</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(item.id)}>
                          <Trash2 className="text-destructive h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </CardContent>
                <div className="bg-card text-card-foreground flex justify-between border-t p-6">
                  <Button variant="outline" disabled={bookinMutation.isPending} onClick={() => setStep(2)}>
                    Zurück
                  </Button>
                  <Button
                    disabled={items.length === 0 || bookinMutation.isPending || isLabelLoading}
                    onClick={submitItems}
                  >
                    {bookinMutation.isPending ? "Wird eingebucht..." : "Jetzt Einbuchen"}{" "}
                    <Save className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </main>
  );
}
